import { cn } from "@design-system/utils";
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";
import { useComposedRefs } from "@radix-ui/react-compose-refs";
import * as React from "react";

import { Button, type ButtonProps } from "../button";

type AlertDialogContextValue = {
  pending: boolean;
  onAbort?: () => void;
};

const AlertDialogContext = React.createContext<AlertDialogContextValue>({
  pending: false,
});

function useAlertDialogContext() {
  return React.useContext(AlertDialogContext);
}

type AlertDialogProps = React.ComponentProps<
  typeof AlertDialogPrimitive.Root
> & {
  /** The confirmed action is in flight. */
  pending?: boolean;
  /** Aborts or rolls back the in-flight action. Supplying it keeps cancel and Escape available while pending. */
  onAbort?: () => void;
};

function AlertDialog({ pending = false, onAbort, ...props }: AlertDialogProps) {
  const context = React.useMemo(
    () => ({ pending, onAbort }),
    [pending, onAbort]
  );

  return (
    <AlertDialogContext.Provider value={context}>
      <AlertDialogPrimitive.Root data-slot="alert-dialog" {...props} />
    </AlertDialogContext.Provider>
  );
}

function AlertDialogTrigger({
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Trigger>) {
  return (
    <AlertDialogPrimitive.Trigger data-slot="alert-dialog-trigger" {...props} />
  );
}

function AlertDialogPortal({
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Portal>) {
  return (
    <AlertDialogPrimitive.Portal data-slot="alert-dialog-portal" {...props} />
  );
}

function AlertDialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Overlay>) {
  return (
    <AlertDialogPrimitive.Overlay
      data-slot="alert-dialog-overlay"
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-110 bg-black/50",
        className
      )}
      {...props}
    />
  );
}

function AlertDialogContent({
  className,
  onEscapeKeyDown,
  ref,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Content>) {
  const { pending, onAbort } = useAlertDialogContext();
  const contentRef = React.useRef<HTMLDivElement>(null);
  const composedRefs = useComposedRefs(contentRef, ref);
  const held = pending && !onAbort;

  // Disabling cancel while held drops focus out of the dialog; put it back.
  React.useEffect(() => {
    if (!held) return;
    const active = document.activeElement;
    if (active && active !== document.body) return;
    contentRef.current?.focus();
  }, [held]);

  return (
    <AlertDialogPortal>
      <AlertDialogOverlay />
      <AlertDialogPrimitive.Content
        data-slot="alert-dialog-content"
        ref={composedRefs}
        onEscapeKeyDown={(event) => {
          onEscapeKeyDown?.(event);
          if (event.defaultPrevented || !pending) return;
          if (onAbort) onAbort();
          else event.preventDefault();
        }}
        className={cn(
          "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 border-border-default fixed top-[50%] left-[50%] z-110 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-8 rounded-lg border p-6 shadow-lg duration-200 sm:max-w-lg",
          className
        )}
        {...props}
      />
    </AlertDialogPortal>
  );
}

function AlertDialogHeader({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-dialog-header"
      className={cn("flex flex-col gap-2", className)}
      {...props}
    />
  );
}

function AlertDialogFooter({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className
      )}
      {...props}
    />
  );
}

function AlertDialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Title>) {
  return (
    <AlertDialogPrimitive.Title
      data-slot="alert-dialog-title"
      className={cn(
        "text-foreground-neutral-emphasised text-lg font-semibold",
        className
      )}
      {...props}
    />
  );
}

function AlertDialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Description>) {
  return (
    <AlertDialogPrimitive.Description
      data-slot="alert-dialog-description"
      className={cn("text-foreground-neutral-muted text-sm", className)}
      {...props}
    />
  );
}

function AlertDialogAction({
  variant = "critical",
  size = "larger",
  isLoading,
  ...props
}: Omit<ButtonProps, "asChild">) {
  const { pending } = useAlertDialogContext();

  return (
    <AlertDialogPrimitive.Action asChild>
      <Button
        data-slot="alert-dialog-action"
        variant={variant}
        size={size}
        isLoading={isLoading ?? pending}
        {...props}
      />
    </AlertDialogPrimitive.Action>
  );
}

function AlertDialogCancel({
  variant = "default",
  size = "larger",
  disabled,
  onClick,
  ...props
}: Omit<ButtonProps, "asChild">) {
  const { pending, onAbort } = useAlertDialogContext();
  const held = pending && !onAbort;

  return (
    <AlertDialogPrimitive.Cancel asChild>
      <Button
        data-slot="alert-dialog-cancel"
        variant={variant}
        size={size}
        disabled={disabled || held}
        onClick={(event) => {
          onClick?.(event);
          if (event.defaultPrevented) return;
          if (pending) onAbort?.();
        }}
        {...props}
      />
    </AlertDialogPrimitive.Cancel>
  );
}

export {
  AlertDialog,
  AlertDialogPortal,
  AlertDialogOverlay,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
};
export type { AlertDialogProps };
